import React, { useEffect, useState } from "react";
import { api, ApiError, type Property } from "../../api";
import { useToast } from "../../ToastContext";
import { LocationCaptureField, type PropertyLocation } from "../shared/LocationCapture";
import { INDIAN_STATES } from "../../indianStates";
import { useLanguage } from "../../i18n";
import { FurnishingItemsEditor, type CustomItemDraft } from "./CustomFurnishingItems";
import { FURNISHING_ITEM_KEYS } from "../../furnishingItems";
import { Select } from "../shared/Select";

const FACILITY_OPTIONS = ["wifi", "meals", "laundry", "powerBackup", "cctv", "parking", "ac", "tv", "washingMachine"];
const RESIDENTIAL_TYPES = ["1RK", "1BHK", "2BHK", "3BHK", "4BHK", "5BHK", "6BHK", "Duplex", "Villa"];
const COMMERCIAL_TYPES = ["Shop", "Office", "Showroom", "Warehouse", "Co-working", "Factory"];
const MEAL_OPTIONS = ["breakfast", "lunch", "dinner"];
const TENANT_TYPE_OPTIONS = ["student", "professional"];
const FURNISHING_STATUSES = ["unfurnished", "semi_furnished", "fully_furnished"] as const;
const COMMUNITY_TYPES = ["gated", "semi_gated", "villa", "apartment", "independent"] as const;
const FLOOR_COMMUNITY_TYPES = ["gated", "semi_gated", "apartment"];

export function EditPropertyForm({
  property,
  onDone,
  onCancel,
}: {
  property: Property;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(property.label);
  const [locality, setLocality] = useState(property.locality);
  const [city, setCity] = useState(property.city ?? "");
  const [state, setState] = useState(property.state ?? "");
  const [pincode, setPincode] = useState(property.pincode ?? "");
  const [type, setType] = useState(property.type ?? "2BHK");
  const [occupancyType, setOccupancyType] = useState<"boys" | "girls" | "co-ed">(
    (property.occupancyType as "boys" | "girls" | "co-ed") ?? "co-ed"
  );
  const [expectedRent, setExpectedRent] = useState(String(property.expectedRent ?? ""));
  const [expectedDeposit, setExpectedDeposit] = useState(String(property.expectedDeposit ?? ""));
  const [maintenance, setMaintenance] = useState(String(property.maintenance ?? ""));
  const [facilities, setFacilities] = useState<string[]>(property.facilities ?? []);
  const [mealPlan, setMealPlan] = useState<string[]>(property.mealPlan ?? []);
  const [preferredTenants, setPreferredTenants] = useState<string[]>(property.preferredTenants ?? []);
  const [furnishingStatus, setFurnishingStatus] = useState<string | null>(property.furnishingStatus ?? null);
  const [communityType, setCommunityType] = useState<string | null>(property.communityType ?? null);
  const [floorNumber, setFloorNumber] = useState(property.floorNumber ?? "");
  const [furnishingItems, setFurnishingItems] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const key of FURNISHING_ITEM_KEYS) init[key] = String(property.furnishingItems?.[key] ?? "");
    return init;
  });
  const [customItems, setCustomItems] = useState<CustomItemDraft[]>(() =>
    Object.entries(property.furnishingItems ?? {})
      .filter(([key]) => !(FURNISHING_ITEM_KEYS as readonly string[]).includes(key))
      .map(([name, qty]) => ({ name, qty: String(qty) }))
  );
  // Starts with every preset "not added" (list opens empty) except ones
  // that already carry a saved quantity — see FurnishingItemsEditor.
  const [removedPresets, setRemovedPresets] = useState<string[]>(() =>
    FURNISHING_ITEM_KEYS.filter((key) => !property.furnishingItems?.[key])
  );
  const [carpetAreaSqft, setCarpetAreaSqft] = useState(String(property.carpetAreaSqft ?? ""));
  const [builtUpAreaSqft, setBuiltUpAreaSqft] = useState(String(property.builtUpAreaSqft ?? ""));
  const [lockInMonths, setLockInMonths] = useState(String(property.lockInMonths ?? ""));
  const [rentEscalationPercent, setRentEscalationPercent] = useState(String(property.rentEscalationPercent ?? ""));
  const [rentEscalationYears, setRentEscalationYears] = useState(String(property.rentEscalationYears ?? ""));
  const [powerBackup, setPowerBackup] = useState<boolean | null>(property.powerBackup ?? null);
  const [powerLoadDetails, setPowerLoadDetails] = useState(property.powerLoadDetails ?? "");
  const [parkingSpots, setParkingSpots] = useState(String(property.parkingSpots ?? ""));
  const [washroomType, setWashroomType] = useState<string | null>(property.washroomType ?? null);
  const [gstRegistrationPossible, setGstRegistrationPossible] = useState<boolean | null>(property.gstRegistrationPossible ?? null);
  const [frontageSignage, setFrontageSignage] = useState<boolean | null>(property.frontageSignage ?? null);
  const [possessionStatus, setPossessionStatus] = useState<string | null>(property.possessionStatus ?? null);
  const [seatsCount, setSeatsCount] = useState(String(property.seatsCount ?? ""));
  const [cabinsCount, setCabinsCount] = useState(String(property.cabinsCount ?? ""));
  const [meetingRoomsCount, setMeetingRoomsCount] = useState(String(property.meetingRoomsCount ?? ""));
  const [location, setLocation] = useState<PropertyLocation | null>(property.location);
  const hasUnits = (property.units?.length ?? 0) > 0;
  const [mapsKey, setMapsKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [benchmark, setBenchmark] = useState<{ insufficientData: boolean; avgRent?: number; scope?: string } | null>(null);
  const toast = useToast();
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/api/config").then(({ googleMapsKey }) => setMapsKey(googleMapsKey)).catch(() => {});
  }, []);

  // Debounced so typing in the type/locality/city fields doesn't fire a
  // request per keystroke — only once things settle for half a second.
  useEffect(() => {
    if (property.category === "pg" || !type) {
      setBenchmark(null);
      return;
    }
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ category: property.category, type });
      if (city.trim()) params.set("city", city.trim());
      if (locality.trim()) params.set("locality", locality.trim());
      api.get(`/api/rent-benchmark?${params.toString()}`).then(setBenchmark).catch(() => setBenchmark(null));
    }, 500);
    return () => clearTimeout(timer);
  }, [property.category, type, city, locality]);

  function toggleFacility(f: string) {
    setFacilities((fs) => (fs.includes(f) ? fs.filter((x) => x !== f) : [...fs, f]));
  }

  function toggleInList(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  async function save() {
    if (!label.trim()) return setError(t("editPropertyForm.errNameRequired"));
    setBusy(true);
    setError("");
    try {
      const payload: any = {
        label,
        locality,
        city: city.trim() || null,
        state: state || null,
        pincode: pincode.trim() || null,
        location,
      };
      // Multi-house properties keep furnishing per house (edited via each
      // house's own form) instead of once for the whole property.
      if (property.category !== "pg" && !hasUnits) {
        payload.furnishingStatus = furnishingStatus;
        payload.communityType = property.category === "commercial" ? null : communityType;
        payload.floorNumber =
          property.category === "commercial"
            ? floorNumber.trim() || null
            : FLOOR_COMMUNITY_TYPES.includes(communityType ?? "")
            ? floorNumber.trim() || null
            : null;
        payload.furnishingItems = {
          ...Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, Number(furnishingItems[k]) || 0])),
          ...Object.fromEntries(customItems.filter((c) => c.name.trim()).map((c) => [c.name.trim(), Number(c.qty) || 0])),
        };
      }
      if (property.category !== "pg") {
        payload.type = type;
        payload.expectedRent = Number(expectedRent) || 0;
        payload.expectedDeposit = Number(expectedDeposit) || 0;
        payload.maintenance = Number(maintenance) || 0;
        if (property.category === "commercial") {
          payload.carpetAreaSqft = carpetAreaSqft.trim() ? Number(carpetAreaSqft) : null;
          payload.builtUpAreaSqft = builtUpAreaSqft.trim() ? Number(builtUpAreaSqft) : null;
          payload.lockInMonths = lockInMonths.trim() ? Number(lockInMonths) : null;
          payload.rentEscalationPercent = rentEscalationPercent.trim() ? Number(rentEscalationPercent) : null;
          payload.rentEscalationYears = rentEscalationYears.trim() ? Number(rentEscalationYears) : null;
          payload.powerBackup = powerBackup;
          payload.powerLoadDetails = powerLoadDetails.trim() || null;
          payload.parkingSpots = parkingSpots.trim() ? Number(parkingSpots) : null;
          payload.washroomType = washroomType;
          payload.gstRegistrationPossible = gstRegistrationPossible;
          payload.frontageSignage = frontageSignage;
          payload.possessionStatus = possessionStatus;
          if (type === "Office" || type === "Co-working") {
            payload.seatsCount = seatsCount.trim() ? Number(seatsCount) : null;
            payload.cabinsCount = cabinsCount.trim() ? Number(cabinsCount) : null;
            payload.meetingRoomsCount = meetingRoomsCount.trim() ? Number(meetingRoomsCount) : null;
          }
        }
      } else {
        payload.occupancyType = occupancyType;
        payload.facilities = facilities;
        payload.mealPlan = mealPlan;
        payload.preferredTenants = preferredTenants;
      }
      await api.patch(`/api/properties/${property.id}`, payload);
      toast(t("editPropertyForm.propertyUpdated"));
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("editPropertyForm.couldNotUpdate"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 10 }}>
      {error && <div className="error-banner">{error}</div>}
      <LocationCaptureField
        mapsKey={mapsKey}
        location={location}
        onChange={setLocation}
        onLocalityDetected={(text, force) => setLocality((prev) => (force || !prev.trim() ? text : prev))}
        onAddressDetected={(parts, force) => {
          setCity((prev) => (force || !prev.trim() ? parts.city : prev));
          setState((prev) => (force || !prev.trim() ? parts.state : prev));
          setPincode((prev) => (force || !prev.trim() ? parts.pincode : prev));
        }}
      />
      <div className="field">
        <label>{t("editPropertyForm.localityLabel")}</label>
        <div className="input-wrap"><input value={locality} onChange={(e) => setLocality(e.target.value)} /></div>
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("addPropertyForm.cityLabel")}</label>
          <div className="input-wrap"><input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("addPropertyForm.cityPlaceholder")} /></div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("addPropertyForm.stateLabel")}</label>
          <Select
            value={state}
            onChange={setState}
            placeholder={t("addPropertyForm.statePlaceholder")}
            options={INDIAN_STATES.map((s) => ({ value: s, label: s }))}
          />
        </div>
      </div>
      <div className="field">
        <label>{t("addPropertyForm.pincodeLabel")}</label>
        <div className="input-wrap">
          <input
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder={t("addPropertyForm.pincodePlaceholder")}
            inputMode="numeric"
          />
        </div>
      </div>
      <div className="field">
        <label>{t("editPropertyForm.propertyNameLabel")}</label>
        <div className="input-wrap"><input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
      </div>

      {property.category !== "pg" ? (
        <>
          <div className="field">
            <label>{t("editPropertyForm.typeLabel")}</label>
            <Select
              value={type}
              onChange={setType}
              groups={[
                { label: t("editPropertyForm.residentialGroup"), options: RESIDENTIAL_TYPES.map((rt) => ({ value: rt, label: rt })) },
                { label: t("editPropertyForm.commercialGroup"), options: COMMERCIAL_TYPES.map((ct) => ({ value: ct, label: ct })) },
              ]}
            />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("editPropertyForm.expectedRentLabel")}</label>
              <div className="input-wrap"><input type="number" value={expectedRent} onChange={(e) => setExpectedRent(e.target.value)} /></div>
              {benchmark && !benchmark.insufficientData && (
                <div className="field-hint" style={{ marginTop: 4 }}>
                  {t("editPropertyForm.rentBenchmarkHint", { amount: `₹${benchmark.avgRent?.toLocaleString("en-IN")}` })}
                </div>
              )}
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("editPropertyForm.expectedDepositLabel")}</label>
              <div className="input-wrap"><input type="number" value={expectedDeposit} onChange={(e) => setExpectedDeposit(e.target.value)} /></div>
            </div>
          </div>
          <div className="field">
            <label>{t("editPropertyForm.maintenanceLabel")}</label>
            <div className="input-wrap"><input type="number" value={maintenance} onChange={(e) => setMaintenance(e.target.value)} /></div>
          </div>
        </>
      ) : (
        <>
          <div className="field">
            <label>{t("editPropertyForm.occupancyLabel")}</label>
            <Select
              value={occupancyType}
              onChange={(v) => setOccupancyType(v as any)}
              options={[
                { value: "boys", label: t("editPropertyForm.occBoys") },
                { value: "girls", label: t("editPropertyForm.occGirls") },
                { value: "co-ed", label: t("editPropertyForm.occCoEd") },
              ]}
            />
          </div>
          <div className="field">
            <label>{t("editPropertyForm.facilitiesLabel")}</label>
            <div className="chip-row">
              {FACILITY_OPTIONS.map((f) => (
                <div
                  key={f}
                  className={`chip${facilities.includes(f) ? " selected" : ""}`}
                  onClick={() => toggleFacility(f)}
                >
                  {t(`editPropertyForm.facility_${f}`)}
                </div>
              ))}
            </div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.mealPlanLabel")}</label>
            <div className="chip-row">
              {MEAL_OPTIONS.map((m) => (
                <div
                  key={m}
                  className={`chip${mealPlan.includes(m) ? " selected" : ""}`}
                  onClick={() => toggleInList(mealPlan, setMealPlan, m)}
                >
                  {t(`addPropertyForm.meal_${m}`)}
                </div>
              ))}
            </div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.preferredTenantsLabel")}</label>
            <div className="chip-row">
              {TENANT_TYPE_OPTIONS.map((tt) => (
                <div
                  key={tt}
                  className={`chip${preferredTenants.includes(tt) ? " selected" : ""}`}
                  onClick={() => toggleInList(preferredTenants, setPreferredTenants, tt)}
                >
                  {t(`addPropertyForm.tenantType_${tt}`)}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {property.category === "commercial" && !hasUnits && (
        <>
          <div className="section-title" style={{ margin: "14px 0 6px" }}>
            {t("addPropertyForm.commercialDetailsHeading")}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.carpetAreaLabel")}</label>
              <div className="input-wrap"><input type="number" value={carpetAreaSqft} onChange={(e) => setCarpetAreaSqft(e.target.value)} /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.builtUpAreaLabel")}</label>
              <div className="input-wrap"><input type="number" value={builtUpAreaSqft} onChange={(e) => setBuiltUpAreaSqft(e.target.value)} /></div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.lockInMonthsLabel")}</label>
              <div className="input-wrap"><input type="number" value={lockInMonths} onChange={(e) => setLockInMonths(e.target.value)} /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.rentEscalationPercentLabel")}</label>
              <div className="input-wrap"><input type="number" value={rentEscalationPercent} onChange={(e) => setRentEscalationPercent(e.target.value)} placeholder="5" /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.rentEscalationYearsLabel")}</label>
              <div className="input-wrap"><input type="number" value={rentEscalationYears} onChange={(e) => setRentEscalationYears(e.target.value)} placeholder="2" /></div>
            </div>
          </div>

          <div className="field">
            <label>{t("addPropertyForm.possessionStatusLabel")}</label>
            <div className="chip-row">
              {["ready", "under_fitout"].map((s) => (
                <div
                  key={s}
                  className={`chip${possessionStatus === s ? " selected" : ""}`}
                  onClick={() => setPossessionStatus(possessionStatus === s ? null : s)}
                >
                  {t(`addPropertyForm.possession_${s}`)}
                </div>
              ))}
            </div>
          </div>

          <div className="field">
            <label>{t("addPropertyForm.washroomTypeLabel")}</label>
            <div className="chip-row">
              {["attached", "shared"].map((w) => (
                <div
                  key={w}
                  className={`chip${washroomType === w ? " selected" : ""}`}
                  onClick={() => setWashroomType(washroomType === w ? null : w)}
                >
                  {t(`addPropertyForm.washroom_${w}`)}
                </div>
              ))}
            </div>
          </div>

          <div className="field">
            <label>{t("addPropertyForm.commercialAmenitiesLabel")}</label>
            <div className="chip-row">
              <div className={`chip${powerBackup ? " selected" : ""}`} onClick={() => setPowerBackup(powerBackup ? null : true)}>
                {t("addPropertyForm.powerBackupChip")}
              </div>
              <div
                className={`chip${gstRegistrationPossible ? " selected" : ""}`}
                onClick={() => setGstRegistrationPossible(gstRegistrationPossible ? null : true)}
              >
                {t("addPropertyForm.gstChip")}
              </div>
              <div className={`chip${frontageSignage ? " selected" : ""}`} onClick={() => setFrontageSignage(frontageSignage ? null : true)}>
                {t("addPropertyForm.frontageChip")}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 2 }}>
              <label>{t("addPropertyForm.powerLoadDetailsLabel")}</label>
              <div className="input-wrap">
                <input
                  value={powerLoadDetails}
                  onChange={(e) => setPowerLoadDetails(e.target.value)}
                  placeholder={t("addPropertyForm.powerLoadDetailsPlaceholder")}
                />
              </div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.parkingSpotsLabel")}</label>
              <div className="input-wrap"><input type="number" value={parkingSpots} onChange={(e) => setParkingSpots(e.target.value)} /></div>
            </div>
          </div>

          {(type === "Office" || type === "Co-working") && (
            <div style={{ display: "flex", gap: 10 }}>
              <div className="field" style={{ flex: 1 }}>
                <label>{t("addPropertyForm.seatsCountLabel")}</label>
                <div className="input-wrap"><input type="number" value={seatsCount} onChange={(e) => setSeatsCount(e.target.value)} /></div>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>{t("addPropertyForm.cabinsCountLabel")}</label>
                <div className="input-wrap"><input type="number" value={cabinsCount} onChange={(e) => setCabinsCount(e.target.value)} /></div>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>{t("addPropertyForm.meetingRoomsCountLabel")}</label>
                <div className="input-wrap"><input type="number" value={meetingRoomsCount} onChange={(e) => setMeetingRoomsCount(e.target.value)} /></div>
              </div>
            </div>
          )}
        </>
      )}

      {hasUnits && (
        <div className="field-hint" style={{ marginBottom: 12 }}>{t("editPropertyForm.perHouseFurnishingHint")}</div>
      )}

      {property.category !== "pg" && !hasUnits && (
        <>
          <div className="field">
            <label>{t("editPropertyForm.furnishingStatusLabel")}</label>
            <div className="chip-row">
              {FURNISHING_STATUSES.map((f) => (
                <div
                  key={f}
                  className={`chip${furnishingStatus === f ? " selected" : ""}`}
                  onClick={() => setFurnishingStatus(furnishingStatus === f ? null : f)}
                >
                  {t(`editPropertyForm.${property.category === "commercial" ? "commercialFurnishing" : "furnishing"}_${f}`)}
                </div>
              ))}
            </div>
          </div>

          {property.category !== "commercial" && (
            <div className="field">
              <label>{t("editPropertyForm.communityTypeLabel")}</label>
              <div className="chip-row">
                {COMMUNITY_TYPES.map((c) => (
                  <div
                    key={c}
                    className={`chip${communityType === c ? " selected" : ""}`}
                    onClick={() => setCommunityType(communityType === c ? null : c)}
                  >
                    {t(`editPropertyForm.community_${c}`)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(property.category === "commercial" || FLOOR_COMMUNITY_TYPES.includes(communityType ?? "")) && (
            <div className="field">
              <label>{t("editPropertyForm.floorNumberLabel")}</label>
              <div className="input-wrap">
                <input
                  value={floorNumber}
                  onChange={(e) => setFloorNumber(e.target.value)}
                  placeholder={t("editPropertyForm.floorNumberPlaceholder")}
                />
              </div>
            </div>
          )}

          <div className="field">
            <label>{t("editPropertyForm.furnishingItemsLabel")}</label>
            <div className="field-hint" style={{ marginBottom: 8 }}>{t("editPropertyForm.furnishingItemsHint")}</div>
            <FurnishingItemsEditor
              presetItems={furnishingItems}
              onPresetChange={(key, value) => setFurnishingItems((prev) => ({ ...prev, [key]: value }))}
              removedPresets={removedPresets}
              onRemovedPresetsChange={setRemovedPresets}
              customItems={customItems}
              onCustomItemsChange={setCustomItems}
            />
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={save}>
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}
